import { Data, List, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import ContactLookup from '../Models/Contacts/ContactLookup';
import Contact from '../Models/Contacts/Contact';
import ContactCriteria from '../Models/Contacts/ContactCriteria';

export interface IContactApiClient {

    /**
     * Gets a paged list of contact lookups
     * @param pageRequest The filter criteria
     * @returns A list of ContactLookups
     */
    getContactsPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<ContactCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ContactLookup>>>;

    /** 
     * Gets a list of contacts. Does not limit the number of results.
     */
    getFilteredContacts(criteria: Model.PartialPlainObject<ContactCriteria>): AxiosPromise<Array<Model.PlainObject<ContactLookup>>>;

    /**
     * Gets a list of contacts that match the search text. Limited to 50 results.
     */
    getContactLookups(searchText: string): Promise<ContactLookup[]>;

    /**
     * Gets a contact with the specified contact id
     * @param contactId The contact id of the contact to fetch
     * @returns The Contact with the given id
     */
    getContact(contactId: number): AxiosPromise<Model.PlainTrackedObject<Contact>>;

    /**
     * Creates a new Contact
     * @param contact The Contact to create
     * @returns The created Contact
     */
    save(contact: Model.PartialPlainObject<Contact>): AxiosPromise<Model.PlainTrackedObject<Contact>>;

    /**
     * Deletes the Contact with the given id
     * @param contactId The id of the Contact to delete
     * @returns A Task
     */
    delete(contactId: number): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class ContactApiClient extends Data.ApiClientBase implements IContactApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/contacts`);
    }

    public getContactsPaged(pageRequest: Model.PartialPlainObject<Data.PageRequest<ContactCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<ContactLookup>>> {
        return this.axios.post(`${this.apiPath}/paged`, pageRequest);
    }

    public getFilteredContacts(criteria: Model.PartialPlainObject<ContactCriteria>): AxiosPromise<Array<Model.PlainObject<ContactLookup>>> {
        return this.axios.post(`${this.apiPath}/filtered`, criteria);
    }

    public async getContactLookups(searchText: string): Promise<ContactLookup[]> {
        const response = await this.axios.get(`${this.apiPath}/lookups?searchText=${encodeURIComponent(searchText)}`);
        return List.fromJSArray(ContactLookup, response.data);
    }

    public getContact(contactId: number): AxiosPromise<Model.PlainTrackedObject<Contact>> {
        return this.axios.get(`${this.apiPath}/${contactId}`);
    }

    public save(contact: Model.PartialPlainObject<Contact>): AxiosPromise<Model.PlainTrackedObject<Contact>> {
        return this.axios.post(`${this.apiPath}/create`, contact);
    }

    public delete(contactId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/${contactId}`);
    }

    // Client only properties / methods
}